import React, { useState, useRef } from "react";
import { X, CheckCircle2, Download, Loader2 } from "lucide-react";
import ReportPrintView from "../../dashboard/components/ReportPrintView";
import { downloadReportPdf } from "../../../utility/downloadReportPdf";

// ── Score colour ──────────────────────────────────────────────────────────────
const scoreColor = (score) => {
  if (score >= 8)  return "#10B981"; // green
  if (score >= 7)  return "#4361EE"; // blue
  if (score >= 5)  return "#F59E0B"; // amber
  if (score >= 4)  return "#F97316"; // orange
  return "#EF4444";                  // red
};

// ── Semicircle gauge ──────────────────────────────────────────────────────────
const SemiGauge = ({ score, max = 10 }) => {
  const cx = 100;
  const cy = 105;
  const r  = 80;
  const strokeW = 14;

  const fraction  = Math.min(Math.max(score / max, 0), 1);

  // Left & right endpoints of the diameter (the "mouth" of the arc)
  const startX = cx - r;  // 20
  const startY = cy;      // 105
  const endX   = cx + r;  // 180
  const endY   = cy;      // 105

  // Partial fill endpoint — sweep counterclockwise from left → right over the top
  // Standard angle: 180° at left, 0° at right; decreases as score increases
  const endAngle    = (1 - fraction) * Math.PI;          // radians
  const ex          = cx + r * Math.cos(endAngle);
  const ey          = cy - r * Math.sin(endAngle);       // SVG y flipped

  const color = scoreColor(score);

  return (
    <svg viewBox="0 0 200 115" width="100%" height="100%">
      {/* Background track */}
      <path
        d={`M ${startX} ${startY} A ${r} ${r} 0 0 1 ${endX} ${endY}`}
        fill="none"
        stroke="#E2E8F0"
        strokeWidth={strokeW}
        strokeLinecap="round"
      />
      {/* Filled arc */}
      {fraction > 0.005 && (
        <path
          d={`M ${startX} ${startY} A ${r} ${r} 0 0 1 ${ex.toFixed(2)} ${ey.toFixed(2)}`}
          fill="none"
          stroke={color}
          strokeWidth={strokeW}
          strokeLinecap="round"
        />
      )}
      {/* Score value */}
      <text
        x={cx} y={cy - 22}
        textAnchor="middle"
        fontSize="28"
        fontWeight="700"
        fill="#1E293B"
        fontFamily="inherit"
      >
        {score}
      </text>
      {/* Label */}
      <text
        x={cx} y={cy - 2}
        textAnchor="middle"
        fontSize="11"
        fill="#64748B"
        fontFamily="inherit"
      >
        Overall Score
      </text>
    </svg>
  );
};

// ── Score row ─────────────────────────────────────────────────────────────────
const ScoreRow = ({ label, score, max = 10 }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
    <span className="text-sm text-brand-text">{label}</span>
    <span className="text-sm font-semibold" style={{ color: scoreColor(score) }}>
      {score}/{max}
    </span>
  </div>
);

// ── Mock data (replace with API) ──────────────────────────────────────────────
// ── Modal ─────────────────────────────────────────────────────────────────────
const AnalysisDetailModal = ({ analysis, user, onClose }) => {
  const [tab, setTab] = useState("summary");
  const [pdfLoading, setPdfLoading] = useState(false);
  const printRef = useRef(null);
  const d = analysis ?? {};

  const pdfCategories = (d.scores ?? []).map((s) => ({
    name: s.label,
    score: s.score,
    maxScore: 10,
    confidence: 0,
    verdict: s.indicator ?? "Neutral",
    description: s.summary ?? "",
  }));

  const pdfResult = d.title ? {
    title: d.title,
    reviewedAt: d.analyzedAt ?? "",
    overallScore: d.overallScore ?? 0,
    categoriesAnalyzed: pdfCategories.length,
    avgConfidence: null,
    toolSummary: d.toolSummary ?? "",
    executiveSummary: d.executiveSummary ?? "",
    categories: pdfCategories,
  } : null;

  const handleExportPdf = async () => {
    if (!printRef.current || !pdfResult) return;
    setPdfLoading(true);
    try {
      await downloadReportPdf(printRef.current, d.title ?? "report");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <>
      {pdfResult && <ReportPrintView ref={printRef} result={pdfResult} softwareName={d.title ?? ""} />}
      {/* Overlay — sits on top of the history modal */}
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-blue-600">Analyze Privacy Policy</h2>
              <p className="text-xs text-brand-muted mt-0.5">
                {user?.name || "User name"} - {user?.email || "Email ID"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-full border-2 border-red-400 text-red-400 hover:bg-red-50 transition-colors flex-shrink-0 mt-0.5"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 flex flex-col gap-5">
          {/* Status card */}
          <div className="border border-surface-border rounded-xl p-4 flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-semibold text-emerald-600">Analysis Complete</span>
              </div>
              <p className="text-base font-bold text-brand-text pl-6">{d.title || "—"}</p>
              <p className="text-xs text-brand-muted pl-6">Analyzed on {d.analyzedAt}</p>
            </div>
            <button
              onClick={handleExportPdf}
              disabled={pdfLoading || !pdfResult}
              className="flex items-center gap-2 bg-primary text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors flex-shrink-0 disabled:opacity-60"
            >
              {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {pdfLoading ? "Generating…" : "Export PDF"}
            </button>
          </div>

          {/* Tabs */}
          <div className="border border-surface-border rounded-xl overflow-hidden">
            <div className="flex gap-0 border-b border-surface-border">
              {["summary", "report"].map((t) => {
                const label = t === "summary" ? "Summary" : "View Report";
                const active = tab === t;
                return (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={`px-5 py-3 text-sm font-medium transition-colors relative ${
                      active ? "text-primary" : "text-brand-muted hover:text-brand-text"
                    }`}
                  >
                    {label}
                    {active && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            {tab === "summary" && (
              <div className="p-6">
                <div className="flex gap-8">
                  {/* Left 60%: score table */}
                  <div className="w-[60%]">
                    <p className="text-sm font-bold text-brand-text mb-3">Policy Overview</p>
                    <div className="flex flex-col">
                      {(d.scores ?? []).map((s) => (
                        <ScoreRow key={s.label} label={s.label} score={s.score} />
                      ))}
                    </div>
                  </div>

                  {/* Right 40%: gauge */}
                  <div className="w-[40%] flex flex-col items-center justify-center gap-3 pl-4">
                    <div className="w-full max-w-[220px]">
                      <SemiGauge score={d.overallScore} />
                    </div>
                    <p className="text-xs text-brand-muted text-center leading-relaxed px-2">
                      Composite rating across data sharing, usage,
                      retention, security &amp; confidentiality
                    </p>
                  </div>
                </div>
              </div>
            )}

            {tab === "report" && (
              <div className="p-5 flex flex-col gap-5">
                {d.executiveSummary && (
                  <div>
                    <p className="text-xs font-bold tracking-widest text-primary uppercase mb-2">Executive Summary</p>
                    <p className="text-sm text-brand-text leading-relaxed">{d.executiveSummary}</p>
                  </div>
                )}
                {d.toolSummary && (
                  <div>
                    <p className="text-xs font-bold tracking-widest text-brand-muted uppercase mb-2">Tool Summary</p>
                    <p className="text-sm text-brand-muted leading-relaxed">{d.toolSummary}</p>
                  </div>
                )}
                {d.scores?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold tracking-widest text-brand-muted uppercase mb-3">Category Details</p>
                    <div className="flex flex-col gap-3">
                      {d.scores.map((s) => (
                        <div key={s.label} className="border border-surface-border rounded-xl p-4">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-semibold text-brand-text">{s.label}</span>
                            <span className="text-sm font-bold" style={{ color: scoreColor(s.score) }}>{s.score}/10</span>
                          </div>
                          {s.indicator && (
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full inline-block mb-1 ${
                              s.indicator === "Good"    ? "bg-emerald-50 text-emerald-600"
                              : s.indicator === "Neutral" ? "bg-amber-50 text-amber-600"
                              : "bg-red-50 text-red-500"
                            }`}>{s.indicator}</span>
                          )}
                          {s.summary && <p className="text-xs text-brand-muted leading-relaxed mt-1">{s.summary}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {!d.executiveSummary && !d.toolSummary && (
                  <p className="text-sm text-brand-muted text-center py-8">No report data available.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default AnalysisDetailModal;
